"use client";

import Link from "next/link";
import Image from "next/image";
import { useEffect, useMemo, useState } from "react";
import { useClientReady } from "@/hooks/useClientReady";
import type React from "react";
import { appendUtmToUrl, collectUtmFromLocation } from "@/lib/tracking";
import { isInstagramInApp } from "@/lib/inAppBrowser";
import pricingService from "@/services/pricing";
import { TarotApiService } from "@/services/tarotApi";
import { trackLandingCtaClick, trackPageView } from "@/services/analytics";
import { config } from "@/config/app";
import { getCurrencyConfig, formatCurrencyFromCents } from "@/config/currency";
import {
  addStartupMark,
  isRuntimeSafeModeEnabled,
} from "@/lib/runtimeDiagnostics";
import { getBaseUrlByLanguage } from "@/lib/seo";

/*
 * Paleta da landing (místico/premium) — manter este conjunto fechado:
 *   títulos ........ #5c1616  (bordô profundo)
 *   corpo .......... #4a3230  (marrom quente)
 *   secundário ..... #8a6a5f  (marrom acinzentado)
 *   dourado claro .. #a3742c  (accent sobre fundo claro)
 *   dourado ........ #d5a253  (accent sobre fundo escuro / filetes)
 *   fundo suave .... #faf6f1  (seções alternadas)
 *   CTA ............ verde esmeralda built-in (emerald-*)
 */

function SparkleIcon({
  className = "",
  color = "#a3742c",
}: {
  className?: string;
  color?: string;
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill={color}
      aria-hidden="true"
      className={className}
    >
      <path d="M12 2c.65 4.9 2.5 7.55 10 10-7.5 2.45-9.35 5.1-10 10-.65-4.9-2.5-7.55-10-10 7.5-2.45 9.35-5.1 10-10z" />
    </svg>
  );
}

function CheckIcon({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className={className}>
      <circle cx="12" cy="12" r="11" fill="#059669" />
      <path
        d="M7 12.5l3.2 3.2L17 9"
        stroke="#ffffff"
        strokeWidth="2.4"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CrossIcon({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className={className}>
      <circle cx="12" cy="12" r="11" fill="#8a1f1f" />
      <path
        d="M8.5 8.5l7 7M15.5 8.5l-7 7"
        stroke="#ffffff"
        strokeWidth="2.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

function GuaranteeIcon({
  type,
  className = "",
}: {
  type: "shield" | "heart" | "sparkle" | "clock" | "refund" | "trust";
  className?: string;
}) {
  const paths = {
    shield: <path d="M12 3 5 6v5c0 4.5 2.9 8.5 7 10 4.1-1.5 7-5.5 7-10V6l-7-3Zm-3.1 9.2 2 2L15.5 9" />,
    heart: <path d="M12 20s-7-4.6-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.4-7 10-7 10Z" />,
    sparkle: <path d="M12 3c.5 3.8 1.9 5.6 5.5 6.5-3.6.9-5 2.7-5.5 6.5-.5-3.8-1.9-5.6-5.5-6.5C10.1 8.6 11.5 6.8 12 3Zm6.5 11c.25 1.9.95 2.8 2.5 3.25-1.55.45-2.25 1.35-2.5 3.25-.25-1.9-.95-2.8-2.5-3.25 1.55-.45 2.25-1.35 2.5-3.25Z" />,
    clock: <><circle cx="12" cy="12" r="8" /><path d="M12 7v5l3.5 2" /></>,
    refund: <><circle cx="12" cy="12" r="8" /><path d="M15.5 9.5A4.5 4.5 0 0 0 8 8l-1.5 1.5M8.5 14.5A4.5 4.5 0 0 0 16 16l1.5-1.5M12 8.5v7M14 10.5h-3a1.5 1.5 0 0 0 0 3h2a1.5 1.5 0 0 1 0 3h-3" /></>,
    trust: <><path d="M5 13.5 8.5 17 12 13.5l3.5 3.5L19 13.5" /><path d="M5 10.5 8.5 7 12 10.5 15.5 7 19 10.5" /></>,
  };

  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      {paths[type]}
    </svg>
  );
}

function ChevronIcon({ className = "" }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      aria-hidden="true"
      className={className}
    >
      <path
        d="M5 7.5l5 5 5-5"
        stroke="#a3742c"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Eyebrow({
  children,
  onDark = false,
}: {
  children: React.ReactNode;
  onDark?: boolean;
}) {
  return (
    <p
      className="text-xs md:text-sm font-semibold uppercase tracking-[0.22em] mb-3"
      style={{ color: onDark ? "#d5a253" : "#a3742c" }}
    >
      {children}
    </p>
  );
}

function CtaButton({
  ariaLabel,
  onClick,
  title,
  subtitle,
}: {
  ariaLabel: string;
  onClick: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  title: string;
  subtitle: string;
}) {
  return (
    <Link
      href="/form"
      aria-label={ariaLabel}
      onClick={onClick}
      className="cta-press block w-full max-w-md mx-auto rounded-2xl px-6 py-4 md:py-5 text-center text-lg md:text-xl font-bold text-white font-subtitle active:scale-[0.98]"
      style={{
        background:
          "linear-gradient(180deg, #10b981 0%, #059669 55%, #047857 100%)",
        boxShadow:
          "0 10px 24px -6px rgba(4,120,87,0.45), inset 0 1px 0 rgba(255,255,255,0.25)",
      }}
    >
      <span className="flex flex-col items-center justify-center leading-tight">
        <span>{title}</span>
        <span className="mt-1.5 text-xs md:text-sm font-medium tracking-normal text-emerald-50/95">
          {subtitle}
        </span>
      </span>
    </Link>
  );
}

export default function HomeClient() {
  const [priceDisplay, setPriceDisplay] = useState<string>(
    config.pricing.fallback.priceDisplay,
  );
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [priceCents, setPriceCents] = useState<number>(
    config.pricing.fallback.priceCents,
  );
  const [standardDeliveryMinutes, setStandardDeliveryMinutes] = useState(120);
  const standardDeliveryTime = useMemo(() => {
    const hours = Math.floor(standardDeliveryMinutes / 60);
    const minutes = standardDeliveryMinutes % 60;
    if (hours && minutes) return `${hours} ${hours === 1 ? "hora" : "horas"} e ${minutes} minutos`;
    if (hours) return `${hours} ${hours === 1 ? "hora" : "horas"}`;
    return `${minutes} minutos`;
  }, [standardDeliveryMinutes]);
  const clientReady = useClientReady();
  const isLandingSafeMode = clientReady &&
    (isRuntimeSafeModeEnabled() || isInstagramInApp());
  const [priceValidUntil] = useState(() =>
    new Date(Date.now() + 1000 * 60 * 60 * 24 * 30).toISOString().split("T")[0],
  );
  const supportContact = config.support;
  const supportContactLabel =
    supportContact.channel === "whatsapp"
      ? `WhatsApp ${supportContact.whatsappDisplay}`
      : `Instagram ${supportContact.instagramHandle}`;
  const supportContactHref =
    supportContact.channel === "whatsapp"
      ? supportContact.whatsappUrl
      : `https://instagram.com/${supportContact.instagramHandle.replace(/^@/, "")}`;

  // Shared CTA click handler: preserves UTMs and attempts external open in in-app browsers
  const handleCtaClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    basePath: string = "/form",
    sectionId: string = "unknown",
    ctaId: string = "primary",
  ) => {
    try {
      // Always target the canonical public domain for external opens
      const CANONICAL_BASE =
        process.env.NEXT_PUBLIC_CANONICAL_BASE_URL ||
        getBaseUrlByLanguage("pt-BR");
      // Force path to /form if empty or root
      const effectiveBase = basePath && basePath !== "/" ? basePath : "/form";
      const path = effectiveBase.startsWith("http")
        ? basePath
        : effectiveBase.startsWith("/")
          ? effectiveBase
          : `/${effectiveBase}`;
      const utm = collectUtmFromLocation();
      const absolute = new URL(path, CANONICAL_BASE).toString();
      const target = appendUtmToUrl(absolute, utm);
      void trackLandingCtaClick({
        pageVariant: "pt-BR",
        sectionId,
        ctaId,
        targetUrl: target,
      });
      // Always prevent default so we control navigation to the canonical domain
      e.preventDefault();
      // External-open attempt disabled to reduce friction in in-app browsers.
      // Always navigate in the current browser context.
      window.location.href = target;
    } catch {
      /* no-op */
    }
  };

  // Preço original ("de"): desconto de 30% sobre o original
  // final = original * 0.7 => original = final / 0.7
  const originalDisplay = useMemo(() => {
    const currencyConfig = getCurrencyConfig();
    const rawOriginalCents = Math.round(priceCents / 0.7);
    const whole = Math.floor(rawOriginalCents / 100);
    const lower90 = whole * 100 + 90;
    const upper90 = (whole + 1) * 100 + 90;
    const originalCents =
      Math.abs(rawOriginalCents - lower90) <=
      Math.abs(upper90 - rawOriginalCents)
        ? lower90
        : upper90;
    return (originalCents / 100).toLocaleString(currencyConfig.locale, {
      style: "currency",
      currency: currencyConfig.code,
    });
  }, [priceCents]);

  useEffect(() => {
    const safeMode = isRuntimeSafeModeEnabled() || isInstagramInApp();
    addStartupMark("home_client_mode_detected", {
      page_variant: "pt-BR",
      safe_mode: safeMode,
    });
  }, []);

  useEffect(() => {
    let mounted = true;
    const safeMode = isRuntimeSafeModeEnabled() || isInstagramInApp();
    (async () => {
      addStartupMark("home_client_boot_start", {
        page_variant: "pt-BR",
        safe_mode: safeMode,
      });
      try {
        await TarotApiService.autoLogin();
        addStartupMark("home_client_auto_login_success");
      } catch {
        /* ignore auth error */
        addStartupMark("home_client_auto_login_failed");
      }

      // Fire page view only after autoLogin attempt completes
      try {
        // Try to recover email/phone from sessionStorage to improve match quality
        const storedEmail =
          typeof window !== "undefined"
            ? sessionStorage.getItem("user_email") ||
              sessionStorage.getItem("mp_payer_email")
            : null;
        const storedPhone =
          typeof window !== "undefined"
            ? sessionStorage.getItem("user_phone")
            : null;
        // On Instagram iOS in-app, delay telemetry a bit to reduce startup race conditions.
        const runTrackPageView = () => {
          void trackPageView({
            email: storedEmail || undefined,
            phone: storedPhone || undefined,
          });
        };
        if (safeMode) {
          window.setTimeout(runTrackPageView, 700);
        } else {
          runTrackPageView();
        }
        addStartupMark("home_client_pageview_scheduled", {
          delayed_ms: safeMode ? 700 : 0,
        });
      } catch {
        /* ignore analytics error */
      }

      try {
        const p = safeMode
          ? await new Promise<
              Awaited<ReturnType<typeof pricingService.getPricingConfig>>
            >((resolve, reject) => {
              window.setTimeout(() => {
                pricingService.getPricingConfig().then(resolve).catch(reject);
              }, 250);
            })
          : await pricingService.getPricingConfig();
        if (!mounted) return;
        // Always format price using frontend currency config
        if (typeof p.price_cents === "number") {
          setPriceCents(p.price_cents);
          setPriceDisplay(formatCurrencyFromCents(p.price_cents));
        }
        if (typeof p.standard_delivery_minutes === "number") {
          setStandardDeliveryMinutes(p.standard_delivery_minutes);
        }
        addStartupMark("home_client_pricing_loaded");
        // Always use frontend translations instead of backend description
        // to ensure proper i18n
      } catch {
        /* keep defaults */
        addStartupMark("home_client_pricing_failed");
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  // Removed separate trackPageView effect to avoid race with autoLogin

  // Structured data (JSON-LD) with dynamic price when available
  const jsonLd = useMemo(() => {
    const currencyConfig = getCurrencyConfig();
    return {
      "@context": "https://schema.org",
      "@type": "Product",
      name: "Carta Canalizada",
      description:
        "Carta canalizada personalizada que revela sentimentos e mensagens profundas através de método exclusivo (Código do Espelho Oculto).",
      brand: {
        "@type": "Brand",
        name: "Carta Canalizada",
      },
      image: [
        // Mantemos referência de imagem estática como fallback SEO (poster)
        `${getBaseUrlByLanguage("pt-BR")}/images/produto-final.jpg`,
      ],
      offers: {
        "@type": "Offer",
        priceCurrency: currencyConfig.code,
        price: (priceCents / 100).toFixed(2),
        availability: "https://schema.org/InStock",
        url: `${getBaseUrlByLanguage("pt-BR")}/`,
        priceValidUntil,
      },
    };
  }, [priceCents, priceValidUntil]);

  return (
    <>
      {/* InAppBrowserBanner intentionally disabled to preserve funnel conversion */}
      <script
        type="application/ld+json"
        // Using dangerouslySetInnerHTML to inject JSON-LD
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <style jsx global>{`
        .section-transition {
          opacity: 1;
          transform: none;
          transition: none;
        }

        .landing-safe-mode .animate-pulse {
          animation: none !important;
        }

        .landing-safe-mode [class*="transition"] {
          transition: none !important;
        }

        .hero-background {
          background-image: url("/images/hero-carta-canalizada-mobile.webp");
          background-position: center top;
          background-size: cover;
        }

        @media (min-width: 1024px) {
          .hero-background {
            background-image: url("/images/hero-carta-canalizada.webp");
            background-position: 66% center;
            filter: contrast(1.06) saturate(1.08);
          }
        }

        /* Micro-interações da landing: o globals.css desliga transition/animation
           globalmente (estabilidade em in-app browsers); estas regras, mais
           específicas, reativam apenas o essencial fora do safe mode. */
        .landing-motion .cta-press {
          transition: transform 0.15s ease !important;
        }

        .landing-motion .faq-chevron {
          transition: transform 0.25s ease !important;
        }

        @media (prefers-reduced-motion: reduce) {
          .landing-motion .cta-press,
          .landing-motion .faq-chevron {
            transition: none !important;
          }
        }
      `}</style>
      <div
        className={`font-subtitle text-foreground${
          isLandingSafeMode ? " landing-safe-mode" : " landing-motion"
        }`}
      >
        {/* Hero Section */}
        <section
          className="section-transition relative overflow-hidden px-4 py-10 sm:px-6 sm:py-12 lg:min-h-[720px] lg:py-8"
          data-reveal-section
          data-debug="hero-section-inline"
          style={{
            background: "#fffaf5",
          }}
        >
          <div className="hero-background absolute inset-0" aria-hidden="true" />
          <div className="absolute inset-0 bg-[#fffaf5]/10 lg:bg-transparent lg:bg-gradient-to-r lg:from-[#fffaf5]/10 lg:via-transparent lg:to-transparent" />
          <div className="relative z-10 mx-auto max-w-[1200px]">
            <div className="grid grid-cols-1 items-center gap-9 lg:min-h-[656px] lg:grid-cols-[500px_1fr] lg:gap-12">
              <div className="relative z-10 px-2 text-center lg:px-5 lg:text-left">
                <div className="mb-5 flex justify-center lg:justify-start">
                  <Image
                    src="/images/logo.webp"
                    alt="Logo do produto Carta Canalizada"
                    width={1040}
                    height={489}
                    priority
                    sizes="(max-width: 640px) 180px, (max-width: 768px) 220px, 260px"
                    className="block h-auto w-[145px] object-contain sm:w-[170px] lg:w-[185px]"
                  />
                </div>

                <h1 className="font-title mx-auto mb-4 max-w-[590px] text-center text-[1.85rem] leading-[1.12] text-[#5c1616] sm:text-[2.25rem] lg:max-w-[520px] lg:text-[2.5rem] lg:leading-[1.12]">
                  Receba uma carta canalizada com tudo o que ele sente, mas
                  nunca teve coragem de dizer.
                </h1>
                <p className="mx-auto mb-5 max-w-[540px] text-center text-base leading-relaxed text-[#6d4238]">
                  Sinta a emoção de receber o que o coração dele guardou em
                  silêncio até agora.
                </p>

                <ul className="mx-auto mb-6 max-w-[560px] space-y-2 text-left lg:mx-0">
                  {[
                    "Receba sua carta em formato digital em seu Email ou WhatsApp (você escolhe)",
                    "Receba também uma resposta direta à sua dúvida, guiada pelo Tarot, com um conselho especial",
                    `Entrega em até ${standardDeliveryTime}, em qualquer horário do dia ou da noite`,
                  ].map((txt, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckIcon className="mt-0.5 h-5 w-5 flex-shrink-0" />
                      <span className="text-sm font-medium leading-snug text-[#6d4238]">
                        {txt}
                      </span>
                    </li>
                  ))}
                </ul>

                <div className="lg:max-w-[410px]">
                  <CtaButton
                  ariaLabel={`Começar agora por ${priceDisplay}`}
                  onClick={(e) =>
                    handleCtaClick(e, "/form", "hero", "hero-main")
                  }
                  title="Quero Minha Carta Agora"
                  subtitle={`Entrega em até ${standardDeliveryTime} • 100% personalizada`}
                  />
                </div>
              </div>

              <div className="relative flex justify-center lg:items-end lg:justify-start lg:pt-[280px]">
                <div className="relative w-full max-w-[650px] lg:max-w-[440px]">
                  <div className="hidden">
                    <Image
                      src="/images/produto-final.jpg"
                      alt="Demonstração do produto Carta Canalizada"
                      fill
                      sizes="(max-width: 1024px) 100vw, 620px"
                      className="object-cover"
                    />
                  </div>

                  {/* Painel de Preço */}
                  <div className="mx-auto mb-6 w-[92%] max-w-[420px] overflow-hidden rounded-[1.5rem] border border-[#d5a253]/35 shadow-[0_18px_34px_-14px_rgba(92,22,22,0.28)] lg:hidden">
                    <Image
                      src="/images/mobile-entrega-carta-canalizada-v2.webp"
                      alt="Carta Canalizada, Resposta Canalizada e Conselho do Tarot"
                      width={1122}
                      height={1402}
                      sizes="(max-width: 640px) 92vw, 420px"
                      className="h-auto w-full"
                    />
                  </div>

                  <div className="relative mx-auto w-[92%] sm:w-[84%] lg:mr-0 lg:w-full">
                    <div
                      className="rounded-[1.35rem] bg-[#f8e9dd]/92 p-4 text-center backdrop-blur-sm sm:p-5"
                      style={{
                        border: "1px solid rgba(213,162,83,0.35)",
                        boxShadow: "0 18px 34px -14px rgba(92,22,22,0.3)",
                      }}
                    >
                      <span
                        className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] md:text-xs font-semibold uppercase tracking-[0.18em] text-[#a3742c]"
                        style={{ background: "rgba(213,162,83,0.12)" }}
                      >
                        <SparkleIcon className="h-3 w-3" />
                        Oferta especial
                      </span>
                      <div className="mt-3 mb-4">
                        <div className="text-sm line-through font-medium text-[#8a6a5f]">
                          {originalDisplay}
                        </div>
                        <div className="mt-1 text-[40px] leading-none font-bold text-emerald-700 sm:text-[46px]">
                          {priceDisplay}
                        </div>
                      </div>
                      <CtaButton
                        ariaLabel={`Iniciar minha leitura com segurança por ${priceDisplay}`}
                        onClick={(e) =>
                          handleCtaClick(
                            e,
                            "/form",
                            "hero-price",
                            "hero-price-main",
                          )
                        }
                        title="Quero Solicitar Agora"
                        subtitle="Receba hoje • Pagamento seguro"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Seção do Problema/Contexto */}
        <section
          className="section-transition relative overflow-hidden px-4 py-12 sm:px-6 sm:py-16"
          data-reveal-section
          data-debug="bordo-section-problema-inline"
          style={{
            background: "#2b0d0d",
          }}
        >
          <Image
            src="/images/fundo-etapa-entenda.webp"
            alt=""
            fill
            sizes="100vw"
            aria-hidden="true"
            className="object-cover"
          />
          <div className="absolute inset-0 bg-[#2b0d0d]/15" />
          <div className="relative z-10 mx-auto max-w-[1200px]">
            <div className="mx-auto max-w-3xl text-center">
              <Eyebrow onDark>Etapa 1 de 3 • Entenda o que você sente</Eyebrow>
              <p className="mb-6 text-base leading-relaxed text-[#fff3e9] sm:text-lg">
                É tarde da noite. Você deitada, olhando o teto, com o celular na
                mão e aquela vontade de mandar “oi, tá acordado?”. Você finge
                que já passou, mas basta tocar “a música de vocês” para tudo
                voltar.
              </p>
              <p className="mb-5 text-base leading-relaxed text-[#fff3e9] sm:text-lg">
                O que dói não é só a ausência: é o silêncio, as mensagens que
                não chegaram e as palavras que ficaram presas. A Carta
                Canalizada, através do Tarô e da intuição, traduz essa voz
                silenciosa e entrega aquilo que ele nunca teve coragem de dizer.
              </p>
            </div>

            <div className="mx-auto max-w-[790px] text-center">
              <div
                className="relative my-7 rounded-[1.5rem] bg-[#fff9f1] p-7 text-center md:my-8 md:p-9"
                style={{
                  border: "1px solid rgba(213,162,83,0.45)",
                  boxShadow: "0 22px 48px -16px rgba(0,0,0,0.58)",
                }}
              >
                <div className="pointer-events-none absolute inset-3 rounded-[1rem] border border-[#d5a253]/35" />
                <h3 className="relative mb-4 font-subtitle text-xl font-bold uppercase tracking-wide text-[#7c2929] md:text-2xl">
                  O que realmente funciona:
                </h3>
                <p className="relative text-base leading-7 text-[#5e3d38]">
                  Enquanto você ainda tenta decifrar sinais confusos, outras
                  mulheres já recebem respostas claras sobre o que ele sente.
                </p>
                <p className="relative mt-4 text-base leading-7 text-[#5e3d38]">
                  Com o nosso método exclusivo, você recebe uma mensagem
                  canalizada que transforma dúvida em clareza e te ajuda a
                  entender, de forma direta, o que ficou sem ser dito.
                </p>
                <div className="relative pt-6">
                  <div
                    className="inline-flex flex-wrap items-center justify-center gap-2 rounded-full px-4 py-2 mx-auto"
                    style={{
                      background: "rgba(213,162,83,0.14)",
                      border: "1px solid rgba(163,116,44,0.45)",
                    }}
                  >
                    <SparkleIcon className="h-4 w-4 flex-shrink-0" />
                    <h3 className="text-base md:text-lg font-subtitle font-bold tracking-wide text-[#5c1616]">
                      Apresentamos a Voz Oculta do Amor
                    </h3>
                    <SparkleIcon className="h-4 w-4 flex-shrink-0" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* Seção Sobre o Produto */}
        <section
          className="section-transition relative overflow-hidden px-4 py-12 sm:px-6 sm:py-16"
          data-reveal-section
          data-debug="sobre-section-plain"
          style={{ background: "#fffaf5" }}
        >
          <Image
            src="/images/fundo-sobre-servico.webp"
            alt=""
            fill
            sizes="100vw"
            aria-hidden="true"
            className="object-cover"
          />
          <div className="relative z-10 mx-auto max-w-[1200px]">
            <div className="grid grid-cols-1 items-center gap-9 lg:grid-cols-[0.94fr_1.06fr] lg:gap-12">
              <div className="text-center">
                <Eyebrow>SOBRE ESTE SERVIÇO</Eyebrow>
                <h2 className="font-title mx-auto mb-4 max-w-[560px] text-3xl leading-snug text-[#5c1616] md:text-4xl">
                  Carta Canalizada: Quando o Tarot traduz a voz de quem você
                  ama.
                </h2>
                <p className="mb-6 text-left text-base leading-relaxed text-[#5e3d38] sm:text-lg">
                  Existe um jeito silencioso de entender o que ele sente, mesmo
                  quando as palavras não chegam. Em meio a emoções confusas, a
                  verdade pode parecer distante. Com o tarô, revelamos o que
                  ficou nas entrelinhas e transformamos dúvida em clareza, carta
                  por carta.
                </p>

                <div
                  className="relative my-7 rounded-[1.35rem] bg-[#fffaf4]/95 p-6 text-center sm:p-7"
                  style={{
                    border: "1px solid rgba(213,162,83,0.35)",
                    boxShadow: "0 16px 36px -14px rgba(92,22,22,0.2)",
                  }}
                >
                  <div className="pointer-events-none absolute inset-3 rounded-[0.9rem] border border-[#d5a253]/35" />
                  <h3 className="relative mb-3 flex flex-wrap items-center justify-center gap-2 font-subtitle text-xl font-bold uppercase tracking-wide text-[#7c2929] md:text-2xl">
                    <SparkleIcon className="h-4 w-4 flex-shrink-0" />
                    Nossa Garantia Para Você
                    <SparkleIcon className="h-4 w-4 flex-shrink-0" />
                  </h3>
                  <p className="relative text-base leading-7 text-[#5e3d38]">
                    Seu pedido será tratado com cuidado, respeito e dentro do
                    prazo combinado. Caso a sua carta não seja entregue em até {standardDeliveryTime}, ou caso a entrega não corresponda ao que foi
                    apresentado nesta página, você poderá nos chamar em até 48
                    horas após o recebimento.
                    <br />
                    <br />
                    Nessas situações, analisaremos com carinho e, se confirmado
                    o problema, faremos a devolução de 100% do valor pago, sem
                    burocracia.
                  </p>
                </div>

                <CtaButton
                  ariaLabel={`Garantir agora por ${priceDisplay}`}
                  onClick={(e) =>
                    handleCtaClick(e, "/form", "about-service", "about-main")
                  }
                  title="Quero Receber Minha Leitura"
                  subtitle="Você escolhe: WhatsApp ou e-mail"
                />
              </div>

              <div className="flex justify-center lg:justify-end">
                <div className="relative w-full max-w-[600px]">
                  <div className="relative overflow-hidden rounded-[1.5rem] border-[5px] border-[#fffaf4] ring-1 ring-[#d5a253]/35 shadow-[0_18px_42px_-14px_rgba(92,22,22,0.35)]">
                    <Image
                      src="/images/produto-sobre-carta-canalizada.webp"
                      alt="Mockup do produto final - Carta Canalizada"
                      width={1440}
                      height={1080}
                      sizes="(max-width: 1024px) 100vw, 600px"
                      className="block h-auto w-full"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* Como Funciona o Método */}
        <section
          className="section-transition relative overflow-hidden px-4 py-12 text-center sm:px-6 sm:py-16"
          data-reveal-section
          data-theme="bordo"
          style={{
            background: "#fffaf5",
          }}
        >
          <div className="relative z-10 mx-auto max-w-[1200px]">
            <Eyebrow>Etapa 2 de 3 • Veja como funciona</Eyebrow>
            <h2 className="font-title mb-2 text-3xl text-[#5c1616] md:text-5xl">
              Como funciona o Método
            </h2>
            <h3 className="mb-8 font-subtitle text-xl font-bold tracking-wide text-[#b8873c] md:text-2xl">
              Voz Oculta do Amor
            </h3>
            <div className="mx-auto mb-9 max-w-3xl space-y-4 text-base leading-relaxed text-[#5e3d38] sm:text-lg">
              <p>
                Existe algo profundamente revelador em ler uma mensagem que soa
                exatamente como ele falaria, com palavras que ele nunca teve
                coragem ou clareza para dizer.
              </p>
              <p>
                Voz Oculta do Amor é a base da Carta Canalizada: uma leitura
                oracular exclusiva que usa as chaves do tarô para decodificar o
                que está reprimido no coração dele. Você não está adivinhando,
                está acessando uma mensagem clara e personalizada sobre o que
                ele sente.
              </p>
            </div>
            <div className="mx-auto grid max-w-[1020px] grid-cols-1 gap-7 md:grid-cols-3 md:gap-4">
              {[
                {
                  label: "Início (criação)",
                  src: "/images/metodo-etapa-tiragem.webp",
                  description:
                    "As cartas revelam a energia e a verdade do momento.",
                },
                {
                  label: "Meio (aplicação)",
                  src: "/images/metodo-etapa-escrita.webp",
                  description:
                    "A mensagem é canalizada e escrita com intenção e cuidado.",
                },
                {
                  label: "Fim (resultados)",
                  src: "/images/metodo-etapa-recebimento.webp",
                  description: "A mensagem chega até você no momento certo.",
                },
              ].map((item, i) => (
                <div key={i} className="flex flex-col">
                  <div className="relative aspect-[4/3] overflow-hidden rounded-[1.2rem] border-[3px] border-[#fffaf5] ring-1 ring-[#d5a253]/25 shadow-[0_14px_30px_-14px_rgba(92,22,22,0.28)]">
                    <Image
                      src={item.src}
                      alt={`Prova social - ${item.label}`}
                      fill
                      sizes="(max-width: 768px) 100vw, 33vw"
                      className="object-cover"
                    />
                  </div>
                  <p className="mt-3 text-center text-sm leading-relaxed text-[#6d4238] md:text-base">
                    {item.description}
                  </p>
                  <span className="mt-2 text-center text-lg leading-none text-[#b8873c]">
                    — ♡ —
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>
        <section
          className="section-transition relative overflow-hidden px-4 py-12 sm:px-6 sm:py-16"
          data-reveal-section
          style={{ background: "#fffaf5" }}
        >
          <Image
            src="/images/fundo-sobre-servico.webp"
            alt=""
            fill
            sizes="100vw"
            aria-hidden="true"
            className="object-cover"
          />
          <div className="relative z-10 mx-auto max-w-[1200px]">
            <div className="mx-auto mb-9 max-w-4xl text-center">
              <Eyebrow>Etapa 3 de 3 • Comprove com experiências reais</Eyebrow>
              <h2 className="font-title mb-3 text-3xl text-[#5c1616] md:text-[44px] md:leading-tight">
                Mulheres Reais. Mensagens Reais. Respostas Reais.
              </h2>
              <p className="text-base leading-relaxed text-[#8a6a5f] md:text-lg">
                Mais de 4.500 mulheres já viveram a experiência da Carta
                Canalizada.
              </p>
            </div>

            <div className="mx-auto grid max-w-[960px] grid-cols-1 gap-6 md:grid-cols-3">
              {[
                "/images/Depoimentos/1.jpg",
                "/images/Depoimentos/2.jpg",
                "/images/Depoimentos/3.jpg",
                "/images/Depoimentos/4.jpg",
                "/images/Depoimentos/5.jpg",
                "/images/Depoimentos/6.jpg",
              ].map((src, i) => (
                <div
                  key={i}
                  className="rounded-[1.35rem] bg-[#fffaf4]/85 p-2 ring-1 ring-[#d5a253]/30 shadow-[0_14px_32px_-14px_rgba(92,22,22,0.2)]"
                >
                  <div className="relative aspect-[4/5] overflow-hidden rounded-[1rem]">
                    <Image
                      src={src}
                      alt={`Depoimento ${i + 1}`}
                      fill
                      sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                      className="object-cover"
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10">
              <CtaButton
                ariaLabel={`Receber minha carta agora por ${priceDisplay}`}
                onClick={(e) =>
                  handleCtaClick(
                    e,
                    "/form",
                    "testimonials",
                    "testimonials-main",
                  )
                }
                title="Quero Viver Essa Experiência"
                subtitle="Mais de 4.500 mulheres já receberam"
              />
            </div>
          </div>
        </section>
        {/* Empilhamento de Valor / Pricing */}
        <section
          className="section-transition relative overflow-hidden px-4 py-12 text-center sm:px-6 sm:py-16"
          data-reveal-section
          data-debug="receber-section-plain"
          style={{ background: "#fffaf5" }}
        >
          <div className="max-w-[1200px] mx-auto">
            <Eyebrow>Próximo passo • Veja exatamente o que você recebe</Eyebrow>
            <h2 className="font-title mb-9 text-3xl text-[#5c1616] md:text-[44px] md:leading-tight">
              Veja Tudo Que Você Vai Receber
            </h2>
            <div
              className="mx-auto grid max-w-[1100px] grid-cols-1 overflow-hidden rounded-[1.35rem] bg-[#fffaf4]/92 p-6 text-left lg:grid-cols-[1.05fr_0.95fr] lg:p-0"
              style={{
                border: "1px solid rgba(213,162,83,0.35)",
                boxShadow: "0 18px 44px -14px rgba(92,22,22,0.18)",
              }}
            >
              <div className="lg:p-8">
                {[
                  {
                    title: "Carta Canalizada Personalizada",
                    description:
                      "Uma mensagem profunda revelando sentimentos, pensamentos e verdades não ditas.",
                  },
                  {
                    title: "Resposta Objetiva à Sua Pergunta",
                    description:
                      "Uma orientação direta sobre sua dúvida específica, guiada pela leitura de Tarot.",
                  },
                  {
                    title: "Conselho Especial do Tarot",
                    description:
                      "Uma mensagem extra para iluminar seu momento e seus próximos passos.",
                  },
                ].map((item) => (
                  <div className="flex items-start gap-3 py-3 first:pt-0 last:pb-0" key={item.title}>
                    <CheckIcon className="mt-1 h-6 w-6 flex-shrink-0" />
                    <div>
                      <span className="text-lg font-semibold leading-relaxed text-[#4a3230]">
                        {item.title}
                      </span>
                      <p className="mt-1 text-sm leading-relaxed text-[#8a6a5f]">
                        {item.description}
                      </p>
                    </div>
                  </div>
                ))}

                <div
                  className="mt-5"
                >
                  <div className="flex items-center justify-between gap-4">
                    <span className="font-bold text-lg md:text-xl text-[#4a3230]">
                      Carta Canalizada Personalizada
                    </span>
                    <div className="text-right">
                      <div className="line-through text-base font-medium text-[#8a6a5f] mb-1">
                        {originalDisplay}
                      </div>
                      <span className="text-emerald-700 text-3xl font-bold">
                        {priceDisplay}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="mx-auto mt-7 max-w-[410px]">
                  <CtaButton
                    ariaLabel={`Receber minha carta canalizada por ${priceDisplay}`}
                    onClick={(e) =>
                      handleCtaClick(e, "/form", "opportunity", "opportunity-main")
                    }
                    title="Quero Solicitar Agora"
                    subtitle="Receba hoje • Pagamento seguro"
                  />
                </div>
              </div>
              <div className="flex flex-col justify-center lg:p-6">
                <div className="hidden">
                  <span className="font-subtitle text-xl font-bold text-[#4a3230]">
                    Carta Canalizada Personalizada
                  </span>
                  <div className="text-right">
                    <div className="mb-1 text-sm font-medium text-[#8a6a5f] line-through">
                      {originalDisplay}
                    </div>
                    <span className="text-emerald-700 text-3xl font-bold">
                      {priceDisplay}
                    </span>
                  </div>
                </div>
              <div className="mt-5 text-center">
                <div
                  data-debug="receber-oportunidade-panel"
                  className="whitespace-pre-line rounded-[1rem] bg-[#f8ecdf]/70 p-5 text-base leading-relaxed text-[#4a3230]"
                  style={{ border: "1px solid rgba(213,162,83,0.3)" }}
                >
                  {`Como cada carta é criada individualmente, com foco, intuição e presença emocional, apenas um número limitado pode ser entregue por dia.

Se o botão de solicitar está disponível agora, significa que ainda temos espaço para a sua leitura neste momento.

Este valor é reservado para quem sente que está alinhada com essa experiência e confia na própria intuição para dar o próximo passo com leveza.

Se o seu coração tem pedido um sinal…
talvez este seja o momento de ouvir. 💫`}
                </div>
              </div>
              </div>
            </div>
          </div>
        </section>
        {/* Comparativo de Ganhos vs Perdas */}
        <section
          className="section-transition relative overflow-hidden px-4 py-12 sm:px-6 sm:py-16"
          data-reveal-section
          data-debug="compare-section-inline"
          style={{
            background:
              "radial-gradient(circle at 5% 8%, rgba(246,203,190,0.36) 0, rgba(246,203,190,0) 22%), radial-gradient(circle at 95% 92%, rgba(244,210,197,0.34) 0, rgba(244,210,197,0) 22%), #fffaf5",
          }}
        >
          <div className="pointer-events-none absolute -left-20 top-8 h-56 w-56 rounded-full border border-[#d5a253]/25" />
          <div className="pointer-events-none absolute -right-24 bottom-4 h-64 w-64 rounded-full border border-[#d5a253]/25" />
          <div className="relative mx-auto max-w-[1200px]">
            <div className="mb-8 flex items-center justify-center gap-3 text-[#d5a253]">
              <span className="h-px w-16 bg-current/60" />
              <SparkleIcon className="h-4 w-4" color="#d5a253" />
              <span className="h-px w-16 bg-current/60" />
            </div>
            <h2 className="font-title mb-9 text-center text-3xl text-[#5c1616] md:text-[44px] md:leading-tight">
              Veja A Diferença Que Isso Fará Na Sua Vida
            </h2>
            <div className="mx-auto grid max-w-[1020px] grid-cols-1 gap-7 md:grid-cols-2 md:gap-9">
              {/* Coluna AGIR */}
              <div
                className="relative rounded-[1.35rem] bg-[#fbfdf9]/90 p-6 pt-11 md:p-7 md:pt-12"
                style={{
                  border: "1px solid rgba(5,150,105,0.25)",
                  boxShadow: "0 16px 38px -14px rgba(4,120,87,0.2)",
                }}
              >
                <CheckIcon className="absolute left-1/2 top-0 h-14 w-14 -translate-x-1/2 -translate-y-1/2 drop-shadow-md" />
                <h3 className="mb-5 text-center font-subtitle text-xl font-bold text-emerald-700 md:text-2xl">
                  SE VOCÊ AGIR HOJE
                </h3>
                <ul className="list-none divide-y divide-[#d5a253]/25">
                  {[
                    "Receber palavras que finalmente rompem o silêncio e dizem o que seu coração sempre precisou ouvir.",
                    "Descobrir o que ele sente sem precisar perguntar ou criar histórias na sua cabeça.",
                    "Ler a verdade dele com a clareza de quem enxerga o que estava preso entre as linhas.",
                    "Tomar decisões com segurança, sabendo exatamente o que existe no coração dele.",
                  ].map((txt, i) => (
                    <li key={i} className="flex items-center gap-4 py-4 first:pt-0 last:pb-0">
                      <CheckIcon className="h-10 w-10 flex-shrink-0" />
                      <span className="font-medium leading-relaxed text-[#38312e]">
                        {txt}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
              {/* Coluna NÃO AGIR */}
              <div
                className="relative rounded-[1.35rem] bg-[#fff9f7]/90 p-6 pt-11 md:p-7 md:pt-12"
                style={{
                  border: "1px solid rgba(92,22,22,0.22)",
                  boxShadow: "0 16px 38px -14px rgba(92,22,22,0.18)",
                }}
              >
                <CrossIcon className="absolute left-1/2 top-0 h-14 w-14 -translate-x-1/2 -translate-y-1/2 drop-shadow-md" />
                <h3 className="mb-5 text-center font-subtitle text-xl font-bold text-[#8a1f1f] md:text-2xl">
                  SE VOCÊ NÃO AGIR
                </h3>
                <ul className="list-none divide-y divide-[#d5a253]/25">
                  {[
                    "Continuar tentando decifrar silêncios e sinais que nunca revelam nada de verdade.",
                    "Adormecer com a sensação de falta, imaginando respostas que ele não diz.",
                    "Esperar mensagens que não chegam e se desgastar emocionalmente no processo.",
                    "Permanecer presa no “e se…?”, carregando dúvidas que poderiam ser resolvidas hoje.",
                  ].map((txt, i) => (
                    <li key={i} className="flex items-center gap-4 py-4 first:pt-0 last:pb-0">
                      <CrossIcon className="h-10 w-10 flex-shrink-0" />
                      <span className="font-medium leading-relaxed text-[#38312e]">
                        {txt}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>
        {/* Garantia Final */}
        <section
          data-debug="garantia-section-inline"
          className="section-transition relative overflow-hidden px-4 py-12 sm:px-6 sm:py-16"
          data-reveal-section
          style={{ background: "#fffaf5" }}
        >
          <Image
            src="/images/fundo-sobre-servico.webp"
            alt=""
            fill
            sizes="100vw"
            aria-hidden="true"
            className="object-cover"
          />
          <div className="relative z-10 mx-auto max-w-[1200px]">
            <div className="mb-5 flex items-center justify-center gap-3 text-[#d5a253]">
              <span className="h-px w-16 bg-current/60" />
              <SparkleIcon className="h-4 w-4" color="#d5a253" />
              <span className="h-px w-16 bg-current/60" />
            </div>
            <h2 className="font-title mb-8 text-center text-3xl text-[#5c1616] md:text-[44px] md:leading-tight">
              Nossa Garantia Para Você
            </h2>
            <div className="mx-auto max-w-[760px]">
              <div
                className="relative mb-5 overflow-hidden rounded-[1.35rem] bg-[#fffaf4]/92 p-5 sm:p-6"
                style={{
                  border: "1px solid rgba(213,162,83,0.35)",
                  boxShadow: "0 18px 40px -16px rgba(92,22,22,0.2)",
                }}
              >
                <div className="pointer-events-none absolute inset-3 rounded-[0.9rem] border border-[#d5a253]/30" />
                <p className="hidden text-base md:text-lg leading-relaxed whitespace-pre-line text-left text-[#4a3230]">
                  {`Depois de tudo o que você viu aqui: os depoimentos, os detalhes e os sinais, talvez uma parte sua ainda precise de uma última confirmação.

E eu entendo.
Por isso, te ofereço o que está ao meu alcance: uma garantia real e comprometida.

A sua Carta Canalizada é um serviço único, feito exclusivamente para você, com total dedicação e verdade.

Seu pedido será tratado com cuidado, respeito e dentro do prazo combinado. Caso a sua carta não seja entregue em até ${standardDeliveryTime}, ou caso a entrega não corresponda ao que foi apresentado nesta página, você poderá nos chamar em até 48 horas após o recebimento.

Nessas situações, analisaremos com carinho e, se confirmado o problema, faremos a devolução de 100% do valor pago, sem burocracia.

Porque confiança não é só uma palavra bonita.
É compromisso com a entrega.
E isso, eu te garanto.`}
                </p>
                <div className="relative divide-y divide-[#d5a253]/30">
                  {[
                    {
                      type: "shield" as const,
                      text: "Depois de tudo o que você viu aqui: os depoimentos, os detalhes e os sinais, talvez uma parte sua ainda precise de uma última confirmação.",
                    },
                    {
                      type: "heart" as const,
                      text: "E eu entendo. Por isso, te ofereço o que está ao meu alcance: uma garantia real e comprometida.",
                    },
                    {
                      type: "sparkle" as const,
                      text: "A sua Carta Canalizada é um serviço único, feito exclusivamente para você, com total dedicação e verdade.",
                    },
                    {
                      type: "clock" as const,
                      text: `Seu pedido será tratado com cuidado, respeito e dentro do prazo combinado. Caso a sua carta não seja entregue em até ${standardDeliveryTime}, ou caso a entrega não corresponda ao que foi apresentado nesta página, você poderá nos chamar em até 48 horas após o recebimento.`,
                    },
                    {
                      type: "refund" as const,
                      text: "Nessas situações, analisaremos com carinho e, se confirmado o problema, faremos a devolução de 100% do valor pago, sem burocracia.",
                    },
                    {
                      type: "trust" as const,
                      text: "Porque confiança não é só uma palavra bonita. É compromisso com a entrega. E isso, eu te garanto.",
                    },
                  ].map(({ type, text }) => (
                    <div key={type} className="flex items-start gap-4 py-4 first:pt-0 last:pb-0 sm:gap-5">
                      <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full border border-[#d5a253]/35 bg-[#fffdf9] text-[#7c2929] shadow-[0_8px_16px_-10px_rgba(92,22,22,0.38)]">
                        <GuaranteeIcon type={type} className="h-6 w-6" />
                      </span>
                      <p className="pt-1 text-sm leading-relaxed text-[#41302e] sm:text-base">
                        {text}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
              <CtaButton
                ariaLabel={`Garantir agora com segurança total por ${priceDisplay}`}
                onClick={(e) =>
                  handleCtaClick(e, "/form", "guarantee", "guarantee-main")
                }
                title="Comprar com Garantia Total"
                subtitle={`Entrega em até ${standardDeliveryTime} ou seu dinheiro de volta`}
              />
            </div>
          </div>
        </section>
        {/* FAQ */}
        <section
          data-debug="faq-section-inline"
          className="section-transition relative overflow-hidden px-5 py-16 sm:px-6 md:py-20"
          data-reveal-section
          style={{ background: "#fffaf5" }}
        >
          <div className="mx-auto max-w-[1200px]">
            <div className="mx-auto mb-4 flex w-52 items-center justify-center gap-2 text-[#c79747]" aria-hidden="true">
              <span className="h-px flex-1 bg-[#e7c98e]" />
              <span className="text-lg leading-none">✦</span>
              <span className="h-px flex-1 bg-[#e7c98e]" />
            </div>
            <h2 className="font-title text-center text-4xl leading-none text-[#5c1616] sm:text-5xl md:text-[3.45rem]">
              Perguntas Frequentes
            </h2>
            <div className="mx-auto mt-8 max-w-[770px] space-y-2">
              {[
                {
                  q: "É seguro solicitar minha Carta Canalizada?",
                  a: (
                    <>
                      <p>
                        Sim. Você pode confirmar a segurança da sua solicitação
                        por meio de sinais públicos e verificáveis:
                      </p>
                      <ul className="mt-3 list-disc space-y-2 pl-5">
                        <li>
                          nosso perfil oficial no Instagram é verificado pela
                          Meta, com publicações diárias, milhares de seguidores e
                          centenas de comentários;
                        </li>
                        <li>
                          o atendimento é realizado por uma empresa formalmente
                          constituída, com políticas e informações legais
                          disponíveis neste site;
                        </li>
                        <li>
                          pagamentos via Pix ou cartão são processados pelo
                          Mercado Pago, que intermedeia a transação em ambiente
                          seguro.
                        </li>
                      </ul>
                    </>
                  ),
                },
                {
                  q: "Posso acessar pelo celular?",
                  a: "Sim! Toda a experiência é entregue de forma digital e a plataforma é 100% responsiva. Você pode acessar de qualquer dispositivo.",
                },
                {
                  q: "Como recebo minha carta canalizada?",
                  a: (
                    <>
                      Dentro de até {standardDeliveryTime} após o pagamento, você receberá sua carta
                      exclusiva diretamente pelo WhatsApp e/ou email. Em caso de
                      dúvidas, nosso suporte também atende pelo{" "}
                      <a
                        href={supportContactHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-semibold underline underline-offset-2"
                      >
                        {supportContactLabel}
                      </a>
                      .
                    </>
                  ),
                },
                {
                  q: "Quem terá acesso aos meus dados?",
                  a: "Aos dados de contato, ninguém. Aos dados do seu pedido, apenas a taróloga que cuidará do seu atendimento. Todos os dados são armazenados temporariamente durante o atendimento e após são automaticamente excluídos.",
                },
                {
                  q: "A carta é realmente personalizada?",
                  a: "Sim. Cada leitura é única, feita por uma taróloga da nossa equipe, com base no método exclusivo.",
                },
                {
                  q: "E se eu não gostar do que receber?",
                  a: "Nós confiamos tanto na qualidade do nosso trabalho que oferecemos garantia de 7 dias: se você sentir que não foi o que esperava, devolvemos seu investimento.",
                },
                {
                  q: "Funciona mesmo se ele(a) estiver distante ou não falar comigo?",
                  a: "Sim. A canalização acessa a energia emocional da pessoa, independente da distância ou da situação atual entre vocês.",
                },
              ].map((item, i) => {
                const isOpen = openFaq === i;
                const panelId = `faq-panel-${i}`;
                return (
                  <div
                    key={i}
                    className="overflow-hidden rounded-[0.9rem] border border-[#ecd2ad] bg-white/60 shadow-[0_7px_18px_-13px_rgba(92,22,22,0.4)]"
                  >
                    <button
                      type="button"
                      aria-expanded={isOpen}
                      aria-controls={panelId}
                      onClick={() => setOpenFaq(isOpen ? null : i)}
                      className="flex min-h-[62px] w-full items-center justify-between gap-5 px-5 py-4 text-left font-subtitle text-lg font-semibold leading-snug text-[#652928] sm:px-8 sm:text-xl"
                    >
                      <span>{item.q}</span>
                      <ChevronIcon
                        className={`faq-chevron h-5 w-5 flex-shrink-0 sm:h-6 sm:w-6${
                          isOpen ? " rotate-180" : ""
                        }`}
                      />
                    </button>
                    {isOpen && (
                      <div
                        id={panelId}
                        className="px-5 pb-5 pt-4 leading-7 text-[#4a3230] sm:px-8 sm:text-lg"
                        style={{
                          borderTop: "1px solid rgba(213,162,83,0.25)",
                        }}
                      >
                        {item.a}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
